import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Loader2, Download, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';
import { Table, TableHeader as UITableHeader, TableRow } from '@/components/ui/table';
import { useConnectionData, FilterOperation } from '@/hooks/useConnectionData';
import { useTimezoneInfo } from '@/hooks/useTimezoneInfo';
import TableSelector from './TableSelector';
import TableHeaderCell from './TableHeader';
import DataTableBody from './DataTableBody';
import DataEmptyState from './DataEmptyState';
import SearchBar from './SearchBar';
import DataTableFooter from './DataTableFooter';
import ActiveFilters from './ActiveFilters';
import MultiColumnFilter from './MultiColumnFilter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TimezoneBadge from '@/components/ui/timezone-badge';
import { exportToCSV } from '@/utils/csvExport';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllOrganisationData } from '@/utils/organization/fetchAllData';
import { isValidTableName } from '@/utils/organization/tables';
import { useToast } from '@/hooks/use-toast';
import {
  startBackgroundExport,
  getExportStatus,
  getAllExportsForConnection,
  downloadExportFile,
  BackgroundExportStatus
} from '@/utils/organization/backgroundExport';
import { GlobalExportManager } from './GlobalExportManager';

interface ConnectionData {
  connectionId: string | null;
  connectorId: string | null;
  availableTables: any[];
  defaultSchema: string;
  defaultTableName: string | null;
  queryResults?: any[] | null;
  selectedTableOverride?: { schema: string; tableName: string } | null;
  queryFilters?: Array<{ column: string; value: string; operation: FilterOperation }> | null;
  connectionStatus?: string;
}

interface ConnectionsDropdownProps {
  connections: Array<{ id: string, connectionId?: string, name: string, type?: string }>;
  selectedConnectionId: string | null;
  onConnectionChange: (connectionId: string) => void;
  capitalizeFirstLetter: (text: string) => string;
}

interface ConnectionDataTableProps {
  organisation: any;
  connectionData: ConnectionData;
  onClearQueryFilters?: () => void;
  availableConnections?: ConnectionsDropdownProps;
  selectedDataSource?: string;
}

const ConnectionDataTable: React.FC<ConnectionDataTableProps> = ({
  organisation,
  connectionData,
  onClearQueryFilters,
  availableConnections,
  selectedDataSource
}) => {
  const [selectedTable, setSelectedTable] = useState<{ schema: string, tableName: string } | null>(null);
  const [selectedTableName, setSelectedTableName] = useState<string | null>(null);
  const [selectedTableDisplayName, setSelectedTableDisplayName] = useState<string | null>(null);
  const [isQueryResultsView, setIsQueryResultsView] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [loadingCsvData, setLoadingCsvData] = useState(false);
  const previousConnectionData = useRef<ConnectionData | null>(null);
  const previousQueryFilters = useRef<Array<{ column: string; value: string; operation: FilterOperation }> | null>(null);
  const [hasCheckedForCsvRegistry, setHasCheckedForCsvRegistry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // New state for background exports
  const [backgroundExports, setBackgroundExports] = useState<BackgroundExportStatus[]>([]);
  const [loadingExports, setLoadingExports] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGlobalExportManager, setShowGlobalExportManager] = useState(false);

  const { toast } = useToast();

  // Memoize connectionIds to prevent infinite loop
  const connectionIds = useMemo(() => {
    return connectionData.connectionId ? [connectionData.connectionId] : [];
  }, [connectionData.connectionId]);

  // Add timezone info hook
  const { getTimezoneForConnection } = useTimezoneInfo(connectionIds);

  // Look up the connection object from organisation.connections
  // This includes searching within csvImports arrays for expanded CSV entries
  const connectionObject = useMemo(() => {
    if (!organisation?.connections || !connectionData.connectionId) return null;

    const connections = Array.isArray(organisation.connections)
      ? organisation.connections
      : Object.values(organisation.connections);

    // First try direct match on parent connection
    let found = connections.find((conn: any) =>
      conn.connectionId === connectionData.connectionId ||
      conn.id === connectionData.connectionId ||
      conn.connectorId === connectionData.connectionId
    );

    // If not found, search within csvImports arrays for expanded entries
    if (!found) {
      for (const conn of connections as any[]) {
        if (conn.csvImports && Array.isArray(conn.csvImports)) {
          const csvMatch = conn.csvImports.find((csv: any, index: number) => {
            const syntheticId = `${conn.id || conn.connectionId || conn.connectorId}__csv_import_${index}`;
            return (
              csv.id === connectionData.connectionId ||
              csv.snapshot_connection_id === connectionData.connectionId ||
              syntheticId === connectionData.connectionId
            );
          });
          if (csvMatch) {
            // Construct a merged connection object preserving parent properties
            // This ensures import_type, ecommerce_subtype etc. are available for getDbNameForConnection
            found = {
              ...conn,
              id: csvMatch.id || csvMatch.snapshot_connection_id || connectionData.connectionId,
              connectionId: csvMatch.id || csvMatch.snapshot_connection_id || connectionData.connectionId,
              file_name: csvMatch.fileName,
              metadata: {
                ...conn.metadata,
                snapshot_connection_id: csvMatch.snapshot_connection_id || csvMatch.id
              }
            };
            break;
          }
        }
      }
    }

    return found || null;
  }, [organisation?.connections, connectionData.connectionId]);

  const isConnectionIncomplete = connectionData.connectionStatus &&
    connectionData.connectionStatus !== 'connected' &&
    connectionData.connectionStatus !== 'active' &&
    connectionData.connectionStatus !== 'error' &&
    connectionData.connectionStatus !== 'broken';

  useEffect(() => {
    if (JSON.stringify(previousConnectionData.current) === JSON.stringify(connectionData) && previousConnectionData.current.availableTables?.length > 0) {
      return;
    }
    previousConnectionData.current = connectionData;

    if (connectionData.selectedTableOverride) {
      setSelectedTable(connectionData.selectedTableOverride);
      setIsQueryResultsView(true);
      setSelectedTableName(connectionData.selectedTableOverride?.tableName);
      // Find display name from available tables
      const tableInfo = connectionData.availableTables?.find(
        (t: any) => t.table_name === connectionData.selectedTableOverride?.tableName
      );
      setSelectedTableDisplayName(tableInfo?.display_name || null);
    } else if (connectionData.defaultTableName) {
      setSelectedTable({
        schema: connectionData.defaultSchema,
        tableName: connectionData.defaultTableName
      });
      setIsQueryResultsView(false);
      setSelectedTableName(connectionData.defaultTableName);
      // Find display name from available tables
      const tableInfo = connectionData.availableTables?.find(
        (t: any) => t.table_name === connectionData.defaultTableName
      );
      setSelectedTableDisplayName(tableInfo?.display_name || null);
    }
    else {
      setSelectedTable(null);
      setSelectedTableName(null);
      setSelectedTableDisplayName(null);
    }

    setCurrentPage(1);
    setTotalRowCount(0);
    setTotalPages(1);
  }, [
    connectionData,
    connectionData.selectedTableOverride,
    connectionData.defaultTableName,
    connectionData.defaultSchema,
    selectedTable
  ]);

  useEffect(() => {
    const loadCsvData = async () => {
      if (!connectionData.connectionId) return;

      try {
        setLoadingCsvData(true);
        const { data, error } = await supabase
          .from('csv_imports')
          .select('*')
          .eq('connection_id', connectionData.connectionId);

        if (error) {
          console.error('Error loading CSV data:', error);
          return;
        }

        if (data) {
          setCsvData(data);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingCsvData(false);
      }
    };

    loadCsvData();
  }, [connectionData.connectionId]);

  // Load background exports
  useEffect(() => {
    const loadBackgroundExports = async () => {
      if (!connectionData.connectionId) return;

      try {
        setLoadingExports(true);
        const result = await getAllExportsForConnection(connectionData.connectionId);
        if (result.success && result.exports) {
          setBackgroundExports(result.exports);
        }
      } catch (err) {
        console.error('Error loading background exports:', err);
      } finally {
        setLoadingExports(false);
      }
    };

    loadBackgroundExports();
  }, [connectionData.connectionId]);

  // Check for csv_table_registry and select it by default if it exists and no other table is selected
  useEffect(() => {
    const checkForCsvTableRegistry = () => {
      if (hasCheckedForCsvRegistry || !connectionData.availableTables || selectedTable) {
        return;
      }

      const registryTable = connectionData.availableTables.find(
        table => table.table_name === 'csv_table_registry'
      );

      if (registryTable) {
        setSelectedTable({
          schema: registryTable.table_schema,
          tableName: registryTable.table_name
        });
        setSelectedTableName(registryTable.table_name);
        setSelectedTableDisplayName(registryTable.display_name || null);
        setIsQueryResultsView(false);
      }

      setHasCheckedForCsvRegistry(true);
    };

    checkForCsvTableRegistry();
  }, [connectionData.availableTables, selectedTable, hasCheckedForCsvRegistry]);

  useEffect(() => {
    if (JSON.stringify(previousQueryFilters.current) !== JSON.stringify(connectionData.queryFilters)) {
      previousQueryFilters.current = connectionData.queryFilters;

      if (connectionData.queryFilters) {
        clearFilters();
      }
    }
  }, [connectionData.queryFilters]);

  // We'll only initialize useConnectionData if it's appropriate, but still provide the hook for all states
  // Compute file_description filter for CSV connections to show only selected file's data
  const fileDescriptionFilter = useMemo(() => {
    const isInventoryTable = connectionData.connectorId?.includes('_inventory_') ||
      connectionData.connectionId?.includes('_inventory_') ||
      selectedTable?.tableName?.includes('inventory_snapshots');

    if (isInventoryTable) {
      // Use connection_id filter for inventory snapshot tables
      const snapshotConnId = connectionObject?.metadata?.snapshot_connection_id;
      return snapshotConnId || null;
    }
    if (connectionObject?.file_name) {
      return `CSV (${connectionObject.file_name})`;
    }
    return null;
  }, [connectionObject?.file_name, connectionObject?.metadata?.snapshot_connection_id, connectionData.connectorId, connectionData.connectionId, selectedTable?.tableName]);

  // For test clones, use source org's data tables and apply data cutoff date
  const effectiveOrgId = (organisation?.is_test_clone && organisation?.source_organisation_id)
    ? organisation.source_organisation_id as string
    : undefined;
  const dataCutoffDate = organisation?.data_cutoff_date as string | undefined;

  const {
    loading,
    error,
    columns,
    tableData,
    filteredAndSortedData,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    availableTables,
    activeFilters,
    addFilter,
    clearFilters,
    getFilterValue,
    setExternalData,
    setExternalFilters,
    initialTableData,
  } = useConnectionData({
    connectionId: isConnectionIncomplete ? null : (connectionData.connectorId || connectionData.connectionId),
    selectedTable,
    organisationId: organisation?.id,
    connection: connectionObject,
    initialAvailableTables: connectionData.availableTables,
    externalData: connectionData.queryResults,
    externalFilters: connectionData.queryFilters,
    page: currentPage,
    pageSize,
    onPaginationChange: (paginationInfo) => {
      setTotalRowCount(paginationInfo.totalRows || 0);
      setTotalPages(paginationInfo.totalPages || 1);
    },
    fileDescriptionFilter,
    effectiveOrgId,
    dataCutoffDate,
  });

  // Custom function to transform table names with spaces instead of underscores
  const getFormattedTableName = (tableName: string) => {
    return tableName?.replace(/_/g, ' ')?.split(' ')?.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  useEffect(() => {
    if (connectionData.queryResults && connectionData.queryResults.length > 0) {
      setExternalData(connectionData.queryResults);
      setIsQueryResultsView(true);
    }
  }, [connectionData.queryResults, setExternalData]);

  useEffect(() => {
    setExternalFilters(connectionData.queryFilters);
  }, [connectionData.queryFilters, setExternalFilters]);

  const handleTableSelect = (tableInfo: string) => {
    const [schema, tableName] = tableInfo.split('.');

    const csvImport = csvData.find(csv => csv.file_name.replace(/\.[^/.]+$/, "") === tableName);

    if (csvImport) {
      setSelectedTable({ schema, tableName });
      setIsQueryResultsView(false);
      setExternalData(csvImport.content);
    } else {
      setSelectedTable({ schema, tableName });
    }
    setSelectedTableName(tableName);

    // Find and set display name from available tables
    const tableMetadata = connectionData.availableTables?.find((t: any) => t.table_name === tableName);
    setSelectedTableDisplayName(tableMetadata?.display_name || null);

    clearFilters();
    setCurrentPage(1);
    setTotalRowCount(0);
    setTotalPages(1);

    if (isQueryResultsView && onClearQueryFilters) {
      onClearQueryFilters();
    }
  };

  const handleRemoveFilter = (column: string, value: string, operation: FilterOperation) => {
    addFilter(column, value, operation);
  };

  const handleClearAllFilters = () => {
    clearFilters();

    if (isQueryResultsView && onClearQueryFilters) {
      onClearQueryFilters();
      setIsQueryResultsView(false);

      if (connectionData.defaultTableName) {
        setSelectedTable({
          schema: connectionData.defaultSchema,
          tableName: connectionData.defaultTableName
        });
        setSelectedTableName(connectionData.defaultTableName);
        // Find display name from available tables
        const tableInfo = connectionData.availableTables?.find(
          (t: any) => t.table_name === connectionData.defaultTableName
        );
        setSelectedTableDisplayName(tableInfo?.display_name || null);
      }
    }
  };

  // New background export handler
  const handleBackgroundExport = async () => {
    if (!selectedTable || !connectionData.connectionId) {
      toast({
        title: 'Export Error',
        description: 'No table selected for export',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);

    try {
      const result = await startBackgroundExport({
        connectionId: connectionData.connectionId,
        schema: selectedTable.schema,
        tableName: selectedTable.tableName,
        filters: activeFilters.length > 0 ? activeFilters : undefined
      });

      if (result.success) {
        toast({
          title: 'Export Started',
          description: 'Large export is processing in the background. You will be notified when it completes.',
        });

        // Refresh the exports list
        const exportsResult = await getAllExportsForConnection(connectionData.connectionId);
        if (exportsResult.success && exportsResult.exports) {
          setBackgroundExports(exportsResult.exports);
        }
      } else {
        toast({
          title: 'Export Failed',
          description: result.error || 'Failed to start background export',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Background export failed:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Updated regular export handler
  const handleExportCsv = async () => {
    if (!selectedTable && !isQueryResultsView) {
      toast({
        title: 'Export Error',
        description: 'No table selected for export',
        variant: 'destructive',
      });
      return;
    }

    // Check if this might be a large export
    if (totalRowCount > 5000 && !isQueryResultsView) {
      toast({
        title: 'Large Export Detected',
        description: `This table has ${totalRowCount} records. Consider using background export for better performance.`,
        action: (
          <Button variant="outline" onClick={handleBackgroundExport}>
            Use Background Export
          </Button>
        ),
      });
      return;
    }

    setIsExporting(true);

    try {
      let dataToExport = [];
      let exportColumns = [];

      if (isQueryResultsView && connectionData.queryResults) {
        dataToExport = connectionData.queryResults;
        exportColumns = columns;
      } else if (selectedTable && (connectionData.connectionId || connectionData.connectorId)) {
        toast({
          title: 'Preparing Export',
          description: 'Fetching all table data for export...',
        });

        const connectionId = connectionData.connectionId || connectionData.connectorId;
        const allData = await fetchAllOrganisationData(
          connectionId,
          selectedTable.schema,
          selectedTable.tableName,
          activeFilters.length > 0 ? activeFilters : undefined
        );

        if (allData.length === 0) {
          toast({
            title: 'Export Warning',
            description: 'No data found to export',
            variant: 'destructive',
          });
          return;
        }

        dataToExport = allData;
        exportColumns = Object.keys(allData[0]);

        toast({
          title: 'Export Ready',
          description: `Preparing to export ${allData.length} records`,
        });
      } else {
        dataToExport = filteredAndSortedData;
        exportColumns = columns;
      }

      if (dataToExport.length > 0) {
        // Get connection type for filename
        const getConnectionType = () => {
          if (availableConnections?.connections && availableConnections.selectedConnectionId) {
            const connection = availableConnections.connections.find(
              (conn: any) => conn.connectionId === availableConnections.selectedConnectionId
            );
            if (connection?.type) {
              return connection.type.toLowerCase();
            }
            if (connection?.name) {
              const name = connection.name.toLowerCase();
              if (name.includes('shopify')) return 'shopify';
              if (name.includes('amazon')) return 'amazon';
            }
          }
          return null;
        };

        const connectionType = getConnectionType();
        const baseFilename = selectedTable
          ? `${selectedTable.tableName}`
          : isQueryResultsView
            ? 'query_results'
            : 'data_export';

        const filename = connectionType ? `${connectionType}_${baseFilename}` : baseFilename;
        const fullFilename = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;

        await exportToCSV(
          dataToExport,
          exportColumns,
          fullFilename,
          selectedTable?.tableName || (isQueryResultsView ? 'query_results' : 'data_export'),
          connectionData.connectionId || undefined
        );

        toast({
          title: 'Export Complete',
          description: `Successfully exported ${dataToExport.length} records`,
        });
      }
    } catch (error) {
      console.error('Export failed:', error);

      // Check if it's a resource limit error and suggest background export
      if (error instanceof Error && (
        error.message.includes('compute resources') ||
        error.message.includes('timeout') ||
        error.message.includes('too large')
      )) {
        toast({
          title: 'Export Too Large',
          description: 'This export is too large for direct download. Use background export instead.',
          action: (
            <Button variant="outline" onClick={handleBackgroundExport}>
              Use Background Export
            </Button>
          ),
        });
      } else {
        toast({
          title: 'Export Failed',
          description: error instanceof Error ? error.message : 'Unknown error occurred during export',
          variant: 'destructive',
        });
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Handle download from background export
  const handleDownloadBackgroundExport = async (exportItem: BackgroundExportStatus) => {
    if (!exportItem.download_url) {
      toast({
        title: 'Download Error',
        description: 'Download URL not available',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get organization name using organisation_id
      const { data: orgData } = await supabase
        .from('organisations')
        .select('name')
        .eq('id', exportItem.organisation_id)
        .maybeSingle();

      const orgName = orgData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'export';
      const filename = `${orgName}_${exportItem.table_name}.csv`;
      await downloadExportFile(exportItem.download_url, filename, undefined, exportItem.export_id);

      toast({
        title: 'Download Started',
        description: 'Your file download has started',
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download Failed',
        description: error instanceof Error ? error.message : 'Failed to download file',
        variant: 'destructive',
      });
    }
  };

  const getHeaderText = () => {
    if (isQueryResultsView) {
      return connectionData.queryFilters && connectionData.queryFilters.length > 0
        ? 'Filtered Query Results'
        : 'Query Results';
    } else if (selectedTableDisplayName) {
      // Use explicit display name if available
      return selectedTableDisplayName;
    } else if (selectedTableName) {
      return getFormattedTableName(selectedTableName);
    }
    return 'Connected Data';
  };

  const getFilteredAvailableTables = () => {
    // Always use connectionData.availableTables as the primary source, fallback to availableTables from hook
    const primaryTables = (connectionData.availableTables && connectionData.availableTables.length > 0)
      ? connectionData.availableTables
      : availableTables;

    const tables = primaryTables.filter(table => table?.table_name !== 'webhook_events' && table?.table_name !== 'items')

    if (!selectedDataSource || selectedDataSource === 'all') {
      const csvTables = csvData.map(csv => ({
        table_schema: 'csv',
        table_name: csv.file_name.replace(/\.[^/.]+$/, ""),
        is_csv: true
      }));

      return [...tables, ...csvTables];
    }

    if (selectedDataSource === 'csv') {
      return csvData.map(csv => ({
        table_schema: 'csv',
        table_name: csv.file_name.replace(/\.[^/.]+$/, ""),
        is_csv: true
      }));
    }

    return tables;
  };

  const filteredAvailableTables = getFilteredAvailableTables();

  // Auto-select first valid table if we have tables but no selection
  useEffect(() => {
    if (!selectedTable && filteredAvailableTables.length > 0 && !loading && !loadingCsvData) {
      const firstValidTable = filteredAvailableTables.find(t => isValidTableName(t.table_name));
      if (firstValidTable) {
        setSelectedTable({
          schema: firstValidTable.table_schema,
          tableName: firstValidTable.table_name
        });
        setSelectedTableName(firstValidTable.table_name);
        setSelectedTableDisplayName(firstValidTable.display_name || null);
      }
    }
  }, [selectedTable, filteredAvailableTables, loading, loadingCsvData]);

  // Add missing pagination handlers
  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading || loadingCsvData) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500 mr-3" />
        <p>Loading connected data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-medium">
            {getHeaderText()}
          </h3>

          {/* Display timezone information for current data source */}
          {connectionData.connectionId && (() => {
            const tzInfo = getTimezoneForConnection(connectionData.connectionId);
            return (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Data Timezone:</span>
                <TimezoneBadge
                  timezone={tzInfo?.detected_timezone || 'UTC'}
                  offset={tzInfo?.timezone_offset || '+00:00'}
                  size="sm"
                />
              </div>
            );
          })()}
        </div>

        <div className="flex items-center gap-3">
          {availableConnections && (
            <div className="flex items-center">
              <Select
                value={availableConnections.selectedConnectionId || ''}
                onValueChange={availableConnections.onConnectionChange}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select connection" />
                </SelectTrigger>
                <SelectContent>
                  {availableConnections.connections.map(connection => (
                    <SelectItem key={connection.connectionId} value={connection.connectionId}>
                      {availableConnections.capitalizeFirstLetter(connection.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <TableSelector
            availableTables={filteredAvailableTables}
            selectedTable={selectedTable}
            onTableSelect={handleTableSelect}
            getDisplayTableName={getFormattedTableName}
          />

          <MultiColumnFilter
            columns={columns}
            activeFilters={activeFilters}
            onAddFilter={addFilter}
            onClearAllFilters={handleClearAllFilters}
            tableData={tableData}
          />

          {/* <SearchBar 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          /> */}

          {filteredAndSortedData.length > 0 && !isConnectionIncomplete && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={totalRowCount > 5000 ? handleBackgroundExport : handleExportCsv}
                disabled={isExporting}
                className="flex items-center gap-1"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isExporting ? 'Exporting...' : (totalRowCount > 5000 ? 'Background Export' : 'Export CSV')}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGlobalExportManager(true)}
                className="flex items-center gap-1"
              >
                <FileText className="h-4 w-4" />
                Export Manager
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Background exports status */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Background Exports</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowExportModal(false)}>
                ×
              </Button>
            </div>

            <div className="space-y-3">
              {backgroundExports.map((exportItem) => (
                <div key={exportItem.export_id} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {exportItem.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {exportItem.status === 'processing' && <Loader2 className="h-5 w-5 animate-spin text-blue-500" />}
                    {exportItem.status === 'queued' && <Clock className="h-5 w-5 text-yellow-500" />}
                    {exportItem.status === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}

                    <div>
                      <p className="font-medium">{exportItem.schema_name}.{exportItem.table_name}</p>
                      <p className="text-sm text-gray-500">
                        {exportItem.status === 'completed' && exportItem.record_count && `${exportItem.record_count} records`}
                        {exportItem.status === 'failed' && exportItem.error_message}
                        {exportItem.status === 'processing' && 'Processing...'}
                        {exportItem.status === 'queued' && 'Queued for processing'}
                      </p>
                    </div>
                  </div>

                  {exportItem.status === 'completed' && exportItem.download_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadBackgroundExport(exportItem)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          {!isQueryResultsView && !isConnectionIncomplete && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Page size:</span>
              <select
                data-testid="page-size-select"
                value={pageSize.toString()}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border-input bg-background text-foreground h-8 rounded-md px-2"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {activeFilters.length > 0 && !isConnectionIncomplete && (
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAllFilters={handleClearAllFilters}
          isQueryFilters={isQueryResultsView}
        />
      )}

      {/* Display the appropriate content based on connection status */}
      {isConnectionIncomplete ? (
        <DataEmptyState
          error={null}
          status={connectionData.connectionStatus}
        />
      ) : error ? (
        <DataEmptyState error={error} status={connectionData.connectionStatus} />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <UITableHeader>
                <TableRow>
                  {columns.map(column => (
                    <TableHeaderCell
                      key={column}
                      column={column}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      filterValue={{ value: getFilterValue(column), operation: 'equals' }}
                      onFilterChange={addFilter}
                    />
                  ))}
                </TableRow>
              </UITableHeader>
              <DataTableBody
                data={filteredAndSortedData}
                columns={columns}
                searchTerm={searchTerm}
              />
            </Table>
          </div>
        </div>
      )}

      {!isConnectionIncomplete && (
        <DataTableFooter
          totalCount={totalRowCount || initialTableData.length || tableData.length}
          filteredCount={filteredAndSortedData.length}
          isQueryResultsView={isQueryResultsView}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
        />
      )}

      {/* Global Export Manager */}
      {connectionData.connectionId && (
        <GlobalExportManager
          connectionId={connectionData.connectionId}
          isOpen={showGlobalExportManager}
          onClose={() => setShowGlobalExportManager(false)}
        />
      )}
    </div>
  );
};

export default ConnectionDataTable;
